import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Checkbox,
  Input,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePathwayCommand } from '../pathways/usePathwayCommand';

/**
 * E34 · PracticeCapabilities + SetPracticeCapability · switches — "Backoffice — Practices — Course capabilities
 * (switches)" (cc-homework, chapter "Pathways — Access, capabilities & course review (backoffice, E34)", screen
 * e34e0012-0001-4000-8000-000000000005): per practice, its three course capabilities with where each comes from —
 * the practice's Stripe plan (E25, the plan reactor) or a switch here (E33) — prescribe courses · customise
 * courses · publish courses (Q-e34-18/20/21/22 A).
 *
 * Behavior (the board): gesture a switch on / off → SetPracticeCapability (granted true / false, source
 * backoffice), i18n backoffice.practices.capabilities.*; format a capability the plan grants shows "from the plan"
 * and its plan column is read-only here — the plan reactor follows the plan; CopingCard's own switch is a separate
 * grant beside it, i18n backoffice.practices.capabilities.fromPlan; help publish courses lets the practice SUBMIT
 * public courses — each version is still reviewed (Backoffice — Course review), i18n
 * backoffice.practices.capabilities.publishHelp. The Practices list (every practice, paged and sorted on the server)
 * is the way in.
 */
type CapabilityRow = { practiceId: string; capability: string; source: string };
type CapabilitySwitch = {
  capability: string;
  fromPlan: boolean;
  backoffice: boolean;
  held: boolean;
};
type PracticeCapabilities = {
  practiceId: string;
  practiceName: string | null;
  capabilities: CapabilityRow[];
  switches: CapabilitySwitch[];
};

const FIELDS = [
  'practiceId',
  'practiceName',
  { capabilities: ['practiceId', 'capability', 'source'] },
  { switches: ['capability', 'fromPlan', 'backoffice', 'held'] },
];

const keyOf = (practiceId: string) => practiceId.replace(/^practices\//, '');

type SortKey = 'name' | 'created' | 'clients';
type SortDir = 'asc' | 'desc';
type PracticeRow = {
  practiceId: string;
  practiceName: string | null;
  createdAt: string | null;
  clientCount: number;
  capabilities: CapabilityRow[];
};
type PracticesPageAnswer = {
  rows: PracticeRow[];
  total: number;
  page: number;
  pageSize: number;
};

const PAGE_SIZE = 50;
const ROW_FIELDS = [
  'practiceId',
  'practiceName',
  'createdAt',
  'clientCount',
  { capabilities: ['practiceId', 'capability', 'source'] },
];
const PAGE_FIELDS = [{ rows: ROW_FIELDS }, 'total', 'page', 'pageSize'];

/**
 * `/backoffice/practices` — EVERY practice (G2, founder Q-g2-2 A, 2026-10-04), paged and sorted on the SERVER, 50 a
 * page: name · Created (default, newest first) · Clients · the capabilities it holds · the Capabilities action. A
 * sort change and a search return to page 1; the checkbox "Only practices with a capability" is the old view.
 * A failed page keeps the rows shown, with a retry (the Homework tab's rule).
 */
export const BackofficePractices = () => {
  const t = useTranslate();
  const locale = useGetLocale()?.() === 'ru' ? 'ru' : 'en';
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [onlyCaps, setOnlyCaps] = useState(false);
  const [sort, setSort] = useState<SortKey>('created');
  const [order, setOrder] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const { data, isFetching, isPreviousData, isError, refetch } =
    useCustom<PracticesPageAnswer>({
      url: '',
      method: 'get',
      errorNotification: false,
      meta: {
        operation: 'backofficePracticesPage',
        fields: PAGE_FIELDS,
        variables: {
          search: { name: 'search', type: 'String', value: search || null },
          holdsCapability: {
            name: 'holdsCapability',
            type: 'Boolean',
            value: onlyCaps,
          },
          sort: { name: 'sort', type: 'BackofficePracticesSort', value: sort },
          order: {
            name: 'order',
            type: 'BackofficePracticesOrder',
            value: order,
          },
          page: { name: 'page', type: 'Int', value: page },
          pageSize: { name: 'pageSize', type: 'Int', value: PAGE_SIZE },
        },
      },
      queryOptions: { keepPreviousData: true },
    });
  // the last page that DID load — a failed page keeps the rows shown, with a retry
  const lastGood = useRef<PracticesPageAnswer | null>(null);
  if (data?.data) lastGood.current = data.data;
  const answer = data?.data ?? lastGood.current;
  // a page past the end answers the last one: the pager moves to the page served
  useEffect(() => {
    if (!isPreviousData && data?.data && data.data.page !== page)
      setPage(data.data.page);
  }, [data, isPreviousData, page]);

  const created = (iso: string | null) =>
    iso
      ? format(parseISO(iso), 'd MMM yyyy', {
          locale: locale === 'ru' ? ru : enUS,
        })
      : '—';
  const sortOrderOf = (key: SortKey) =>
    sort === key
      ? order === 'asc'
        ? ('ascend' as const)
        : ('descend' as const)
      : null;
  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="backoffice-practices"
    >
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.practices.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.practices.intro')}
        </Typography.Paragraph>
      </div>
      <Space size="large" wrap align="center">
        <div
          data-testid="backoffice-practices-search"
          style={{ width: 480, maxWidth: '100%' }}
        >
          <Input.Search
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onSearch={(v) => {
              setSearch(v.trim());
              setPage(1); // a search returns to page 1
            }}
            placeholder={t('backoffice.practices.search')}
            allowClear
            enterButton
          />
        </div>
        <Checkbox
          checked={onlyCaps}
          onChange={(e) => {
            setOnlyCaps(e.target.checked);
            setPage(1);
          }}
          data-testid="backoffice-practices-only-capability"
        >
          {t('backoffice.practices.onlyCapability')}
        </Checkbox>
      </Space>
      {isError ? (
        <Alert
          type="error"
          showIcon
          message={
            answer
              ? t('backoffice.practices.pageLoadFailed')
              : t('backoffice.practices.loadFailed')
          }
          action={
            <Button
              size="small"
              onClick={() => void refetch()}
              data-testid="backoffice-practices-retry"
            >
              {t('backoffice.practices.retry')}
            </Button>
          }
          data-testid="backoffice-practices-load-failed"
        />
      ) : null}
      {/* the server's total, OUTSIDE antd's pager: antd draws no pager for a total of 0, so "Practices: 0" lived nowhere
          and the last count stayed on screen (G2 run, 2026-10-04) */}
      <Typography.Text type="secondary" data-testid="backoffice-practices-total">
        {t('backoffice.practices.pageTotal', { total: answer?.total ?? 0 })}
      </Typography.Text>
      <Table<PracticeRow>
        rowKey="practiceId"
        loading={isFetching}
        dataSource={answer?.rows ?? []}
        pagination={{
          // the SERVER pages — the table only shows the page it was given
          current: answer?.page ?? page,
          pageSize: PAGE_SIZE,
          total: answer?.total ?? 0,
          showSizeChanger: false,
          hideOnSinglePage: false,
        }}
        // the server sorts: a sort change returns to page 1; clearing the sorter returns to the default
        onChange={(pager, _f, sorter, extra) => {
          if (extra.action === 'sort') {
            const one = Array.isArray(sorter) ? sorter[0] : sorter;
            const key = one?.columnKey as SortKey | undefined;
            if (key && one?.order) {
              setSort(key);
              setOrder(one.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSort('created');
              setOrder('desc');
            }
            setPage(1);
          } else {
            setPage(pager.current ?? 1);
          }
        }}
        locale={{ emptyText: t('backoffice.practices.empty') }}
        onRow={(r) =>
          ({
            'data-testid': `backoffice-practice-row-${keyOf(r.practiceId)}`,
          } as never)
        }
        columns={[
          {
            title: t('backoffice.practices.name'),
            key: 'name',
            sorter: true,
            sortOrder: sortOrderOf('name'),
            sortDirections: ['ascend', 'descend', 'ascend'],
            render: (_: unknown, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {r.practiceName ?? r.practiceId}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.practiceId}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t('backoffice.practices.created'),
            key: 'created',
            sorter: true,
            sortOrder: sortOrderOf('created'),
            sortDirections: ['descend', 'ascend', 'descend'],
            render: (_: unknown, r) => (
              <span
                data-testid={`backoffice-practice-created-${keyOf(
                  r.practiceId,
                )}`}
              >
                {created(r.createdAt)}
              </span>
            ),
          },
          {
            title: t('backoffice.practices.clients'),
            key: 'clients',
            sorter: true,
            sortOrder: sortOrderOf('clients'),
            sortDirections: ['descend', 'ascend', 'descend'],
            align: 'right',
            render: (_: unknown, r) => (
              <span
                data-testid={`backoffice-practice-clients-${keyOf(
                  r.practiceId,
                )}`}
              >
                {r.clientCount}
              </span>
            ),
          },
          {
            title: t('backoffice.practices.capabilitiesColumn'),
            key: 'caps',
            render: (_: unknown, r) =>
              r.capabilities.length ? (
                <Space size={4} wrap>
                  {r.capabilities.map((c) => (
                    <Tag
                      key={`${c.capability}-${c.source}`}
                      color={c.source === 'plan' ? 'blue' : 'purple'}
                    >
                      {t(
                        `backoffice.practices.capabilities.name.${c.capability}`,
                      )}{' '}
                      ·{' '}
                      {t(
                        `backoffice.practices.capabilities.source.${c.source}`,
                      )}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">
                  {t('backoffice.practices.none')}
                </Typography.Text>
              ),
          },
          {
            title: '',
            key: 'open',
            render: (_: unknown, r) => (
              <Link
                to={`/backoffice/practices/${encodeURIComponent(
                  keyOf(r.practiceId),
                )}`}
                data-testid={`backoffice-practice-open-${keyOf(r.practiceId)}`}
              >
                {t('backoffice.practices.open')}
              </Link>
            ),
          },
        ]}
      />
    </Space>
  );
};

/** `/backoffice/practices/:practiceKey` — the three capabilities, the plan's grant beside CopingCard's switch. */
export const BackofficePracticeCapabilities = () => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { practiceKey } = useParams();
  const practiceId = `practices/${practiceKey ?? ''}`;
  const { run } = usePathwayCommand();
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useCustom<PracticeCapabilities>(
    {
      url: '',
      method: 'get',
      errorNotification: false,
      queryOptions: { enabled: Boolean(practiceKey) },
      meta: {
        operation: 'practiceCapabilities',
        fields: FIELDS,
        variables: {
          practiceId: { name: 'practiceId', type: 'ID!', value: practiceId },
        },
      },
    },
  );
  if (isLoading) return <Spin />;
  const practice = data?.data ?? null;
  if (isError || !practice) {
    return (
      <Alert
        type="error"
        showIcon
        message={t('backoffice.practices.capabilities.notFound')}
      />
    );
  }

  const flip = async (capability: string, granted: boolean) => {
    setFailure(null);
    setBusy(capability);
    const result = await run(
      'setPracticeCapability',
      { practiceId, capability, granted },
      t('backoffice.practices.capabilities.failed'),
    );
    setBusy(null);
    if (!result.ok) {
      setFailure(result.message);
      await refetch();
      return;
    }
    message.success(
      t(
        granted
          ? 'backoffice.practices.capabilities.on'
          : 'backoffice.practices.capabilities.off',
      ),
    );
    await refetch();
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="backoffice-practice-capabilities"
    >
      <Link to="/backoffice/practices" data-testid="backoffice-practice-back">
        {t('backoffice.practices.capabilities.back')}
      </Link>
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.practices.capabilities.title')}
        </Typography.Title>
        <Typography.Paragraph
          type="secondary"
          style={{ marginBottom: 0 }}
          data-testid="backoffice-practice-name"
        >
          {t('backoffice.practices.capabilities.subtitle', {
            name: practice.practiceName ?? practice.practiceId,
            key: keyOf(practice.practiceId),
          })}
        </Typography.Paragraph>
      </div>
      {failure ? (
        <Alert
          type="error"
          showIcon
          message={failure}
          data-testid="backoffice-practice-capability-error"
        />
      ) : null}
      <Table<CapabilitySwitch>
        rowKey="capability"
        pagination={false}
        dataSource={practice.switches}
        onRow={(r) =>
          ({
            'data-testid': `backoffice-capability-row-${r.capability}`,
          } as never)
        }
        columns={[
          {
            title: t('backoffice.practices.capabilities.capability'),
            key: 'capability',
            render: (_: unknown, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>
                  {t(`backoffice.practices.capabilities.name.${r.capability}`)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {r.capability === 'publishCourses'
                    ? t('backoffice.practices.capabilities.publishHelp')
                    : t(
                        `backoffice.practices.capabilities.help.${r.capability}`,
                      )}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: t('backoffice.practices.capabilities.fromPlanColumn'),
            key: 'plan',
            render: (_: unknown, r) =>
              r.fromPlan ? (
                <Tag
                  color="blue"
                  data-testid={`backoffice-capability-plan-${r.capability}`}
                >
                  {t('backoffice.practices.capabilities.fromPlan')}
                </Tag>
              ) : (
                <Typography.Text
                  type="secondary"
                  data-testid={`backoffice-capability-plan-${r.capability}`}
                >
                  {t('backoffice.practices.capabilities.notFromPlan')}
                </Typography.Text>
              ),
          },
          {
            title: t('backoffice.practices.capabilities.switchColumn'),
            key: 'switch',
            render: (_: unknown, r) => (
              <Switch
                checked={r.backoffice}
                loading={busy === r.capability}
                onChange={(on) => flip(r.capability, on)}
                aria-label={t(
                  `backoffice.practices.capabilities.name.${r.capability}`,
                )}
                data-testid={`backoffice-capability-switch-${r.capability}`}
              />
            ),
          },
        ]}
      />
      <Typography.Paragraph
        type="secondary"
        data-testid="backoffice-practice-capabilities-footer"
      >
        {t('backoffice.practices.capabilities.footer')}
      </Typography.Paragraph>
    </Space>
  );
};
